/**
 * Error tracking module.
 *
 * Reports errors to Sentry (production) + console + sessionStorage (admin debugging).
 */
import * as Sentry from '@sentry/react';

interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  [key: string]: unknown;
}

interface TrackedError {
  message: string;
  stack?: string;
  context: ErrorContext;
  timestamp: string;
  url: string;
}

const MAX_STORED_ERRORS = 50;
const STORAGE_KEY = 'frankly_error_log';

/**
 * Set the current user on Sentry so errors are associated with their account.
 */
export function identifyUser(userId: string, email?: string): void {
  Sentry.setUser({ id: userId, email: email || undefined });
}

/**
 * Clear user identity (call on logout).
 */
export function clearUser(): void {
  Sentry.setUser(null);
}

/**
 * Coerce arbitrary thrown values into a real Error with a useful message.
 *
 * PostgrestError and similar plain-object errors stringify to "[object Object]"
 * via `String(err)`, losing the actual code/message/details/hint. This helper
 * extracts those fields so Sentry sees a meaningful title instead of
 * "[object Object]". The original payload is preserved on `cause` and passed
 * to Sentry as `extra` so the full shape is available when debugging.
 */
function coerceError(error: unknown): { error: Error; rawFields: Record<string, unknown> } {
  if (error instanceof Error) {
    return { error, rawFields: {} };
  }

  // Plain object — likely a PostgrestError or similar { message, code, details, hint }
  if (error && typeof error === 'object') {
    const obj = error as Record<string, unknown>;
    const message = typeof obj.message === 'string' && obj.message.length > 0
      ? obj.message
      : JSON.stringify(error).slice(0, 200);
    const err = new Error(message);
    return {
      error: err,
      rawFields: {
        code: obj.code,
        details: obj.details,
        hint: obj.hint,
        status: obj.status,
        statusCode: obj.statusCode,
      },
    };
  }

  return { error: new Error(String(error)), rawFields: {} };
}

/**
 * Report an error for tracking. Call this from error boundaries,
 * catch blocks, and mutation onError callbacks.
 */
export function reportError(error: unknown, context: ErrorContext = {}): void {
  const { error: err, rawFields } = coerceError(error);

  const tracked: TrackedError = {
    message: err.message,
    stack: err.stack,
    context,
    timestamp: new Date().toISOString(),
    url: window.location.href,
  };

  // Always log to console in all environments
  console.error('[ErrorTracking]', tracked.message, tracked.context);

  // Report to Sentry with structured fields from the original error preserved
  // as extra context (so PostgrestError code/details/hint aren't lost).
  Sentry.captureException(err, { extra: { ...context, ...rawFields } });

  // Store in sessionStorage for admin debugging
  try {
    const existing: TrackedError[] = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    existing.push(tracked);
    // Keep only the most recent errors
    if (existing.length > MAX_STORED_ERRORS) {
      existing.splice(0, existing.length - MAX_STORED_ERRORS);
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch {
    // sessionStorage might be full or unavailable
  }
}

/**
 * Retrieve stored errors (useful for admin debugging panel).
 */
export function getStoredErrors(): TrackedError[] {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}
